import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface EvidenceType {
  id: number
  name: string
  media_kind: string | null
  status: number
  usage_count: number
}

function kindBadge(kind: string | null) {
  if (kind === "video") {
    return (
      <span className="bg-hpi-pale-blue text-hpi-dark-blue inline-block rounded px-2 py-0.5 text-xs font-semibold">
        V
      </span>
    )
  }
  if (kind === "photo") {
    return (
      <span className="bg-hpi-pale-gray text-hpi-dark-gray inline-block rounded px-2 py-0.5 text-xs font-semibold">
        P
      </span>
    )
  }
  return <span className="text-hpi-med-gray text-xs">—</span>
}

interface Props {
  evidence_types: EvidenceType[]
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/evidence_types" },
  { title: "Evidence Types", href: "/admin/evidence_types" },
]

export default function Index({ evidence_types }: Props) {
  const handleDelete = (id: number, usageCount: number) => {
    const message =
      usageCount > 0
        ? "This type is in use. It will be deactivated instead of deleted. Continue?"
        : "Are you sure you want to delete this type?"

    if (confirm(message)) {
      router.delete(`/admin/evidence_types/${id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Evidence Types" />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Evidence Types</h1>
          <Link
            href="/admin/evidence_types/new"
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Type
          </Link>
        </div>

        {evidence_types.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  ID
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Name
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Kind
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  In Use
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {evidence_types.map((et) => (
                <tr key={et.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {et.id}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {et.name}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {kindBadge(et.media_kind)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {et.usage_count > 0 ? et.usage_count : ""}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/admin/evidence_types/${et.id}/edit`}
                      className="text-hpi-active-blue mr-2 hover:underline"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(et.id, et.usage_count)}
                      className="text-hpi-red hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No evidence types found.</p>
        )}
      </div>
    </AppLayout>
  )
}
